import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Direction } from '@mui/material/styles'
import i18n from './i18n'
import { resolveInitialLocale, type LocaleCode } from './resolveLocale'

/**
 * The seven launch languages, mirrored from the API's SiteLocale enum.
 *
 * Kept in the client as a constant rather than fetched, because the language switcher must
 * render before the first API call resolves. `GET /api/v1/site/languages` returns the same
 * list and is the authority if the two ever drift.
 *
 * No flag emoji here: Windows renders regional-indicator pairs as the letters "GB", "FI" and
 * so on rather than as flags. Flags come from {@link FlagIcon} as inline SVG instead.
 */
export const SUPPORTED_LOCALES = [
  { code: 'en', nativeName: 'English', englishName: 'English', dir: 'ltr' },
  { code: 'fi', nativeName: 'Suomi', englishName: 'Finnish', dir: 'ltr' },
  { code: 'ru', nativeName: 'Русский', englishName: 'Russian', dir: 'ltr' },
  { code: 'uk', nativeName: 'Українська', englishName: 'Ukrainian', dir: 'ltr' },
  { code: 'he', nativeName: 'עברית', englishName: 'Hebrew', dir: 'rtl' },
  { code: 'de', nativeName: 'Deutsch', englishName: 'German', dir: 'ltr' },
  { code: 'pl', nativeName: 'Polski', englishName: 'Polish', dir: 'ltr' },
  { code: 'et', nativeName: 'Eesti', englishName: 'Estonian', dir: 'ltr' },
] as const

export type { LocaleCode }

const STORAGE_KEY = 'lakeside.locale'

interface LocaleContextValue {
  locale: LocaleCode
  direction: Direction
  setLocale: (locale: LocaleCode) => void
  locales: typeof SUPPORTED_LOCALES
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

export function LocaleProvider({ children }: { children: ReactNode }) {
  // i18next has already resolved the same value at module load, so the first paint and the
  // provider agree from the very first render.
  const [locale, setLocaleState] = useState<LocaleCode>(resolveInitialLocale)

  const direction: Direction = useMemo(
    () => (SUPPORTED_LOCALES.find((l) => l.code === locale)?.dir === 'rtl' ? 'rtl' : 'ltr'),
    [locale],
  )

  const setLocale = useCallback((next: LocaleCode) => {
    setLocaleState(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Not being able to remember the choice is survivable.
    }
  }, [])

  useEffect(() => {
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale)
    }
  }, [locale])

  const value = useMemo(
    () => ({ locale, direction, setLocale, locales: SUPPORTED_LOCALES }),
    [locale, direction, setLocale],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used inside a LocaleProvider')
  }
  return context
}
